<template>
  <v-navigation-drawer
    app
    :clipped="clipped"
    :width="width"
    :color="color"
    v-model="drawer"
    v-bind="$attrs"
  >
    <div
      v-if="$vuetify.breakpoint.smAndDown && showProfileMobile"
      class="ml-0 mb-3 pr-1"
      style="border-bottom: 1px solid lightgrey"
    >
      <v-expansion-panels flat>
        <v-expansion-panel>
          <v-expansion-panel-header class="d-flex align-end pr-5 pt-5">
            <div id="nav-bar--user-details">
              <InitialsIcon
                :given-name="profileName"
              />
              <h2 class="pt-2 subtitle-1">
                {{ profileName }}
              </h2>
              <v-list-item-subtitle class="pt-1 ads-dark-60 caption">
                {{ profileJobTitle }}
              </v-list-item-subtitle>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-list>
              <v-list-item
                v-for="(item, i) in profileMenuItems"
                :key="i"
                :to="item.route"
                @click="item.clickHandler ? item.clickHandler() : noop()"
                :id="item.id"
              >
                <v-list-item-icon>
                  <v-icon
                    v-if="item.icon && !item.notificationCount"
                    size="28"
                    :class="{'material-icons-outlined': item.iconOutlined}"
                  >
                    {{ item.icon }}
                  </v-icon>
                  <div
                    class="notification-counter d-flex align-center justify-center"
                    v-if="item.notificationCount"
                  >
                    <span>{{ item.notificationCount }}</span>
                  </div>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ item.title }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                @click="$emit('logout')"
                class="mt-2"
              >
                <v-list-item-icon>
                  <v-icon class="nav-bar--logout-icon">
                    mdi-logout-variant
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>
                    <span class="nav-bar--logout-text">Log out of all DoE apps</span>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>

    <slot name="preMenu" />

    <v-list
      nav
      class="mt-2"
    >
      <v-list-item
        v-for="item in items"
        :key="item.title"
        :to="item.route"
        :exact="!!item.exact"
        @click="item.clickHandler ? item.clickHandler() : noop()"
        :class="{'pl-4': !item.items, 'pl-0': item.items, 'expandable': item.items, 'mb-1': !item.items}"
        :id="item.id"
        :ripple="!isGroupHeader(item)"
        :tabindex="isGroupHeader(item) ? -1 : 0"
      >
        <!-- single menu item -->
        <v-row
          v-if="!item.items"
          class="pl-3 align-center"
        >
          <v-list-item-icon>
            <v-icon
              v-if="item.icon && !item.notificationCount"
              size="28"
              :class="{'material-icons-outlined':item.iconOutlined}"
            >
              {{ item.icon }}
            </v-icon>
            <div
              class="notification-counter d-flex align-center justify-center"
              v-if="item.notificationCount"
            >
              <span>{{ item.notificationCount }}</span>
            </div>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item-content>
        </v-row>

        <!-- Expandable group sections -->
        <v-row
          v-if="item.items"
          class="align-center nav-bar--menu-group"
        >
          <v-expansion-panels
            accordion
            flat
          >
            <v-expansion-panel>
              <v-expansion-panel-header
                class="pt-0 pb-0"
                focusable
              >
                <v-row class="pl-1">
                  <v-list-item-icon>
                    <v-icon
                      size="28"
                      :class="{'material-icons-outlined':item.iconOutlined}"
                    >
                      {{ item.icon }}
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.title }}</v-list-item-title>
                  </v-list-item-content>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="mb-1">
                <v-list nav>
                  <v-list-item
                    class="mt-1"
                    v-for="subItem in item.items"
                    :key="subItem.title"
                    :to="subItem.route"
                    :id="subItem.id"
                    :exact="!!item.exact"
                    @click="subItem.clickHandler ? subItem.clickHandler() : noop()"
                  >
                    <v-list-item-content>{{ subItem.title }}</v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-row>
      </v-list-item>
    </v-list>
    <slot name="postMenu" />
  </v-navigation-drawer>
</template>

<script>
  import InitialsIcon from '../InitialsIcon';

  export default {
    name: "NavigationDrawer",
    props: {
      color: {
        type: String,
        default: '#FFFFFF'
      },
      width: {
        type: String,
        default: '300'
      },
      // default clipped to true
      clipped: {
        type: Boolean,
        default: true
      },
      items: {
        type: Array,
        default: () => []
      },
      showProfileMobile: {
        type: Boolean,
        default: false
      },
      profileName: {
        type: String,
        default: ''
      },
      profileJobTitle: {
        type: String,
        default: ''
      },
      profileMenuItems: {
        type: Array,
        default: () => []
      }
    },
    components: {
      InitialsIcon
    },
    data() {
      return {
        drawer: null
      };
    },
    created() {
      this.$eventHub.$on('toggleNavigationDrawer', this.toggleNavigationDrawer);
    },
    methods: {
      toggleNavigationDrawer() {
        this.drawer = !this.drawer;
      },
      noop() {
        return;
      },
      isGroupHeader(item) {
        return item.items != null && item.items.length > 0;
      }
    }
  };
</script>

<style lang="scss" scoped>
  @import '../../styles/variables.scss';

  ::v-deep .v-expansion-panel-content__wrap {
    padding: 0px;
  }

  .v-navigation-drawer.theme--light .v-list .v-list-item {
    color: $ads-dark !important;
    margin: 0px 8px;
    border-radius: 4px;
    min-height: 48px;
    &:before {
      opacity: 0;
    }

    // active state
    &.v-list-item--active {
      .theme--light.v-icon {
        color: $ads-navy;
        margin-bottom: 3px;
      }
      .v-list-item__content {
        color: $ads-navy !important;
        .v-list-item__title {
          color: $ads-navy !important;
        }
      }

      background-color: $ads-custom-navy-2;
      &:before {
        opacity: 0;
      }
    }

    // default states
    .theme--light.v-icon {
      color: $ads-dark;
      margin-bottom: 3px;
    }
    .v-list-item__content {
      color: $ads-dark !important;
      .v-list-item__title {
        color: $ads-dark !important;
        overflow: visible;
      }
    }
    .v-list-item__icon {
      color: $ads-dark;
      margin: 0px;
      margin-right: 21px;
      align-self: auto;
    }
  }
  .v-navigation-drawer.theme--dark{
    background-color:$ads-dark !important;
    .v-list {
    padding: 0;
    .v-list-item{
    margin: 0px 8px;
    border-radius: 4px;
    min-height: 48px;
    &:not(.expandable):hover{
      background-color: $ads-06dp;
    }
    &:before {
      opacity: 0;
    }
    }
    // active state
    .v-list-item--active {
      background-color: $ads-navy-dark !important;
      color: $ads-dark;
      .theme--dark .v-icon {
        margin-bottom: 3px;
      }
      &:before {
        opacity: 0;
      }
    }
    .v-list-item--link:focus {
      border: 1px solid $ads-navy-dark;
    }
    // default states
    .theme--dark .v-icon {
      margin-bottom: 3px;
    }
    .v-list-item__content {
      .v-list-item__title {
        overflow: visible;
      }
    }
    .v-list-item__icon {
      margin: 0px;
      margin-right: 21px;
      align-self: auto;
    }
  }
  }
  .nav-bar--menu-group .v-item-group.v-expansion-panels {
    margin-left: 12px;
    margin-right: 4px;
    z-index: 0;
    .v-expansion-panel-header {
      border-radius: 4px;
      margin: 0px;
      padding-right: 16px;
      &:hover {
        background-color: $ads-light-20;
      }
      &:focus {
        background-color: rgba(0,0,0,0);
        border: 1px solid $ads-navy;
      }
      &:before {
        background-color: transparent;
      }
      &.v-expansion-panel-header--active {
        // override parent's min-height to default v-list-item height
        min-height: 48px;
      }

      ::v-deep .v-expansion-panel-header__icon .v-icon {
        color: $ads-dark;
      }
    }
    .v-expansion-panel-content .v-list-item {
      margin-left: 0px;
      margin-right: 0px;
      .v-list-item__content {
        padding-left: 57px;
      }
    }
  }
  .v-navigation-drawer.theme--dark .v-expansion-panels {
    margin-left: 12px;
    margin-right: 4px;
    z-index: 0;
    background-color: transparent !important;
    .v-expansion-panel{
      background-color: transparent !important;
    }
    .v-expansion-panel-header {
      border-radius: 4px;
      margin: 0px;
      padding-right: 16px;
      background-color: $ads-dark;
      &:hover {
        background-color: $ads-06dp !important;
      }
      &:focus {
        border:1px solid $ads-navy-dark;
      }
      &:before {
        background-color: transparent;
      }
      &.v-expansion-panel-header--active {
        // override parent's min-height to default v-list-item height
        min-height: 48px;
        background-color:transparent;
      }
      ::v-deep .v-expansion-panel-header__icon .v-icon {
        color: $ads-light-20 !important;
      }
    }
    .v-expansion-panel-content .v-list-item {
      margin-left: 0px;
      margin-right: 0px;
      .v-list-item__content {
        padding-left: 57px;
      }
    }
  }
  .v-navigation-drawer.theme--light .v-list {
    padding: 0;
    .v-list-item.v-list-item--link:not(.expandable):hover {
      background-color: $ads-light-20;
    }
    .v-list-item.v-list-item--link:focus {
      border: 1px solid $ads-navy;
    }
  }

  .notification-counter {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background-color: $ads-error-red;
    color: white;
    font-size: 16px;
  }

  .nav-bar--logout-text, .nav-bar--logout-icon {
    color: $ads-error-red !important;
  }
</style>
